<script lang="ts">
  import Cross from "../icons/Cross.svelte";
  import { createDialog, melt } from "@melt-ui/svelte";
  import type { Snippet } from "svelte";
  import type { ClassValue } from "svelte/elements";
  import { writable, type Writable } from "svelte/store";
  import { scale } from "svelte/transition";

  interface Props {
    open?: Writable<boolean>;
    preventClose?: boolean;
    showCloseButton?: boolean;
    trigger?: Snippet;
    title?: Snippet;
    description?: Snippet;
    secondary_description?: Snippet;
    actions?: Snippet;
    children?: Snippet;
    class?: ClassValue;
  }

  let {
    open = $bindable(writable(false)),
    preventClose = false,
    showCloseButton = false,
    trigger,
    title,
    description,
    secondary_description,
    actions,
    children,
    class: className,
    ...rest
  }: Props = $props();

  const {
    elements: {
      trigger: triggerElement,
      overlay,
      content,
      title: titleElement,
      description: descriptionElement,
      close,
      portalled,
    },
  } = createDialog({
    forceVisible: true,
    open,
    // svelte-ignore state_referenced_locally
    escapeBehavior: preventClose ? "ignore" : "close",
    // svelte-ignore state_referenced_locally
    closeOnOutsideClick: !preventClose,
  });
</script>

{#if trigger}
  <div {...$triggerElement} use:$triggerElement.action>
    {@render trigger?.()}
  </div>
{/if}

{#if $open}
  <div {...$portalled} use:$portalled.action>
    <div
      {...$overlay} use:$overlay.action
      class="fixed inset-0 z-[70] bg-modal-overlay opacity-0 transition-opacity duration-[150ms] ease-linear"
      class:opacity-100={open}
    ></div>

    <div
      {...rest}
      transition:scale={{ duration: 200, start: 0.96 }}
      {...$content} use:$content.action
      class={[
        "fixed top-1/2 left-1/2 z-[70] flex max-h-[85vh] max-w-[640px] -translate-x-1/2 -translate-y-1/2 flex-col gap-6 overflow-y-auto rounded-xl bg-mono-000 p-6 shadow-border-popover",
        className,
      ]}
    >
      {#if showCloseButton && !preventClose}
        <button
          type="button"
          {...$close} use:$close.action
          class="absolute top-2.5 right-2.5 z-10 flex h-8 w-8 items-center justify-center text-foreground-tertiary transition-colors duration-200 hover:text-foreground-primary focus:outline-hidden"
          aria-label="Close modal"
        >
          <Cross />
        </button>
      {/if}

      {#if title || description}
        <div class="flex flex-col gap-3">
          {#if title}
            <h2 {...$titleElement} use:$titleElement.action class="text-xl tracking-[-0.02em]">
              {@render title?.()}
            </h2>
          {/if}

          {#if description}
            <p
              {...$descriptionElement} use:$descriptionElement.action
              class="text-sm leading-[22px] text-foreground-secondary"
            >
              {@render description?.()}
            </p>
          {/if}
        </div>
      {/if}

      {#if secondary_description}
        {@render secondary_description?.()}
      {/if}

      {#if children}
        {@render children()}
      {/if}

      {#if actions}
        <div class="flex items-center justify-end gap-2">
          {@render actions?.()}
        </div>
      {/if}
    </div>
  </div>
{/if}
